import FormError from 'cl-form/FormError';
import {FieldInfo, FieldsInfo, FormInfo} from 'cl-form/FormSections/consts';
import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useMemo, useCallback, useRef, useEffect} from 'react';
import BasicPage, {SubPage} from 'cl-layout/BasicPage';
import Button from '@material-ui/core/Button';
import {withRouter, WithRouterProps} from 'react-router';
import FormContextProvider, {FormContextConsumer} from 'cl-form/FormContext/FormContextProvider';
import FormSectionsWithFields from 'cl-form/FormSections/FormSectionsWithFields';
import {columnsInfoToFieldsInfo} from 'cl-table/redux/actions';
import {
  clearCache,
  createProduct,
  getProductRow,
  getProductsColumnsData,
  getProductSections,
  ProductRowData,
  updateProduct,
} from './utils';
import {diff} from 'deep-object-diff';
import RefreshIcon from '@material-ui/icons/Refresh';
import SaveIcon from '@material-ui/icons/Save';
import GeneralActions from '../../shared/ActionsManager/GeneralActions/GeneralActions';
import {GENERAL_ACTIONS_TYPES} from '../../shared/ActionsManager/ActionsTypes';
import {ACTION_DISPLAY_TYPES} from '../../shared/ActionsManager/ActionsManagerConsts';
import {browserHistory} from 'react-router';
import routes from '../../App/routeConstants';
import * as _ from 'lodash';
import VariationsProductSection from './CustomSections/VariationsProductSection';
import {addDynamicDialog} from '../../crazyEditor/redux/modules/panel/panel';
import ProductEditVariationOptionsDialog, {
  ProductEditVariationOptionsDialogComputedProps,
} from './ProductVariationOptionsDialog';
import FormSwitch from 'cl-form/FormSwitch/FormSwitch';
import createComputed from 'cl-form/createComputed';
import {AddVariationFormData} from '../../shared/components/Dialogs/AddVariationDialog/AddVariationDialog';
import {updateVariationsInStore} from '../../shared/ActionsManager/VariationsActions/VariationsActionsHelpers';
import {PRODUCT_LISTINGS_COUNT, PRODUCT_LISTINGS_TABLE, PRODUCT_VARIATIONS, PRODUCT_VARIATIONS_SWITCH} from './consts';
import ListingsProductSection from './CustomSections/ListingsProductSection';
import ProductListingsCount from './ProductListingsCount';
import {StoreState} from '../../App/initialStore';

export const PRODUCT_FORM_NAME = 'editCreateProduct';

type ProductFormData = ProductRowData & {Images?: string[]; Variations?: object; VariationsSwitch?: boolean};

function clearCacheIfNeededNested(data, formInfo: FormInfo) {
  clearCacheIfNeeded(data, formInfo);
  _.forEach(data[PRODUCT_VARIATIONS], variation => {
    clearCacheIfNeeded(variation, formInfo);
  });
}

function clearCacheIfNeeded(data, formInfo: FormInfo) {
  const fieldsByName: {[fieldName: string]: FieldInfo} = _.keyBy(formInfo.fields, 'name');
  if (
    _.some(data, (value, fieldName) => {
      return (
        fieldsByName[fieldName] &&
        fieldsByName[fieldName].type === 'select' &&
        !_.some(fieldsByName[fieldName].options, {value})
      );
    })
  ) {
    clearCache();
  }
}

const prepareVariationIds = data => {
  let index = 0;
  const currentVariations = data ? data[PRODUCT_VARIATIONS] : [];
  return _.isEmpty(currentVariations)
    ? data
    : {
        ...data,
        ...{Variations: _.keyBy(currentVariations, variation => (variation['_id'] ? variation['_id'] : index++))},
      };
};

const keepUnderscoreFields = (initialValues, difference) => {
  _.forEach(initialValues, (fieldValue, fieldName) => {
    if (_.startsWith(fieldName, '_')) {
      difference[fieldName] = fieldValue;
    }
  });
  return difference;
};

// for some reason it wont let me use PRODUCT_VARIATIONS in the generic definition...
const variationsSwitchComponent = createComputed<AddVariationFormData, 'Variations'>({
  fieldNames: [PRODUCT_VARIATIONS],
  propsGenerator: formValues => ({checked: !_.isEmpty(formValues[PRODUCT_VARIATIONS])}),
})((props: any) => {
  return <FormSwitch {...props} />;
});

async function getDefaultValues() {
  const columnsData = await getProductsColumnsData();
  const fieldsWithDefaultValue = _.pickBy(columnsData, 'defaultValue');
  return _.mapValues(fieldsWithDefaultValue, (data: FieldInfo) => data.defaultValue);
}

const ProductPage: React.FunctionComponent<WithRouterProps> = ({params: {id}}) => {
  const dispatch = useDispatch();
  const formConfig = useMemo(() => ({form: PRODUCT_FORM_NAME}), []);
  const fieldsInfo = useRef<FieldsInfo<any>>();

  const getFormInfo = useCallback(async () => {
    fieldsInfo.current = columnsInfoToFieldsInfo(await getProductsColumnsData());
    return {fields: addTablesToFieldsInfo(fieldsInfo.current), sections: await getProductSections(), id: '_id'};
  }, []);

  const productId = useSelector((state: StoreState) => _.get(state.form, `${PRODUCT_FORM_NAME}.values._id`));

  useEffect(() => {
    if (productId && id !== productId) {
      browserHistory.replace({pathname: `${routes.MyAccount.ProductBase}/${productId}`});
    }
  }, [productId]);

  const getInitialValues = useCallback(async () => (id ? await getProductRow(id) : await getDefaultValues()), [id]);

  const onCreate = useCallback(async (data, formInfo) => {
    clearCacheIfNeededNested(data, formInfo);
    const product = await createProduct(data);
    if (product._id) {
      _.defer(() => browserHistory.replace({pathname: `${routes.MyAccount.ProductBase}/${product._id}`}));
    }
    return product;
  }, []);

  const onUpdate = useCallback(
    async (initialValues, data, formInfo) => {
      const initialValuesWithVariationIds = prepareVariationIds(initialValues);
      if (_.isEmpty(data[PRODUCT_VARIATIONS])) {
        data = {...data, [PRODUCT_VARIATIONS]: []};
      }
      let difference: Partial<ProductFormData> = diff(initialValuesWithVariationIds, prepareVariationIds(data));
      difference = keepUnderscoreFields(initialValues, difference);
      if (difference.Images) {
        difference.Images = data.Images;
      }
      _.forEach(difference.Variations, (variation, id) => {
        difference.Variations[id] = _.isUndefined(variation)
          ? {delete: true}
          : keepUnderscoreFields(_.get(initialValuesWithVariationIds.Variations, id), variation);
      });
      clearCacheIfNeededNested(data, formInfo);
      return (await updateProduct(id, {
        product: difference,
        activityMeta: {
          task: PRODUCT_LISTINGS_TABLE,
          refresh: [PRODUCT_LISTINGS_TABLE],
          sourceType: 'Update Product',
          sourceName: 'Update Product',
        },
      })).updatedProduct;
    },
    [id]
  );

  const handleSwitchChange = useCallback(async (event, checked: boolean) => {
    if (checked) {
      dispatch(
        addDynamicDialog<ProductEditVariationOptionsDialogComputedProps>(ProductEditVariationOptionsDialog, {
          fieldsInfo: fieldsInfo.current,
        })
      );
    } else {
      updateVariationsInStore([]);
    }
  }, []);

  const addTablesToFieldsInfo = (fieldsInfo: FieldsInfo): FieldsInfo => {
    const fields: FieldsInfo<any> = [];
    _.forEach(fieldsInfo, info => {
      if (info.name === PRODUCT_VARIATIONS) {
        fields.push({
          name: PRODUCT_VARIATIONS_SWITCH,
          section: info.section,
          component: variationsSwitchComponent,
          xs: 12,
          label: 'This product has variations',
          componentProps: {
            onChange: handleSwitchChange,
            labelPlacement: 'start',
            explanationText: 'Add available product options like color or size.',
          },
        });
        fields.push({...info, component: VariationsProductSection});
      } else if (info.name === PRODUCT_LISTINGS_COUNT) {
        fields.push({
          ...info,
          countComponent: ProductListingsCount,
          component: ListingsProductSection,
        });
      } else {
        fields.push(info);
      }
    });
    return fields;
  };

  return (
    <FormContextProvider<ProductFormData>
      formConfig={formConfig}
      getFormInfo={getFormInfo}
      getInitialValues={getInitialValues}
      onCreate={onCreate}
      onUpdate={onUpdate}
    >
      <FormContextConsumer>
        {({sections, buttonProps: {submit, reset}, errorMeta: {scrollTo}}) => (
          <BasicPage backButton scrollTo={scrollTo ? scrollTo : undefined}>
            <SubPage
              title={id ? 'Edit product' : 'Create new product'}
              contentWidth="md"
              actions={{
                topRight: (
                  <GeneralActions
                    id="cla-btn"
                    types={[GENERAL_ACTIONS_TYPES.OPEN_DELETE_SINGLE_PRODUCTS_DIALOG]}
                    displayType={ACTION_DISPLAY_TYPES.FLAT_BUTTON}
                    additionalActionData={id}
                  />
                ),
                bottomRight: (
                  <Button id="cla-btn-save-product" variant="contained" color="primary" {...submit}>
                    <SaveIcon id="cla-icon-save-product" />
                    save product
                  </Button>
                ),
                bottomLeft: (
                  <Button id="cla-btn-reset-changes" {...reset}>
                    <RefreshIcon id="cla-icon-reset-changes" />
                    reset changes
                  </Button>
                ),
              }}
            >
              <FormError />
              <FormSectionsWithFields sections={sections} />
            </SubPage>
          </BasicPage>
        )}
      </FormContextConsumer>
    </FormContextProvider>
  );
};

export default withRouter(ProductPage);
